/**
 * CredentialHelpTab Component
 * Displays help content, setup instructions, and documentation for a credential type
 */

import React from 'react';
import { ExternalLink, CheckCircle2, AlertCircle, Lightbulb } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getCredentialDefinition } from '@/utils/credentialDefinitions';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';

interface CredentialHelpTabProps {
  /** Credential type to display help for */
  credentialType: string;
}

/**
 * Help tab content showing documentation and setup instructions
 *
 * @example
 * <CredentialHelpTab credentialType="sql_server" />
 */
export const CredentialHelpTab: React.FC<CredentialHelpTabProps> = ({
  credentialType,
}) => {
  const definition = getCredentialDefinition(credentialType);
  const { helpContent } = definition;
  const backendUrl = useGetBackendUrl();

  // Replace {backend-url} placeholder with actual backend URL
  const interpolate = (text: string) => text.replace(/\{backend-url\}/g, backendUrl);

  return (
    <div className="space-y-6">
      {/* Description */}
      <div>
        <p className="text-sm leading-relaxed">{helpContent.description}</p>
      </div>

      {/* Setup Steps */}
      {helpContent.setupSteps.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            Setup Steps
          </h4>
          <ol className="space-y-2 ml-6">
            {helpContent.setupSteps.map((step, index) => (
              <li key={index} className="text-sm text-muted-foreground list-decimal">
                {interpolate(step)}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Required Fields */}
      {helpContent.requiredFields.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-orange-500" />
            Required Fields
          </h4>
          <div className="flex flex-wrap gap-2">
            {helpContent.requiredFields.map((field, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs rounded-md bg-muted text-muted-foreground"
              >
                {field}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tips */}
      {helpContent.tips && helpContent.tips.length > 0 && (
        <Alert>
          <Lightbulb className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              {helpContent.tips.map((tip, index) => (
                <p key={index} className="text-sm">
                  {interpolate(tip)}
                </p>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* External Links */}
      {helpContent.links && helpContent.links.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-3">Documentation</h4>
          <div className="space-y-2">
            {helpContent.links.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CredentialHelpTab;
